import { useMemo } from "react";
import { Entity } from "@common/types/entity";
import { useEntities } from "@hooks/useEntities";
import { useBusinessNonMockEntityIds } from "@hooks/useBusinessEntityIds";
import { useEntityIdsWithAccrualLedger } from "@api/ledger.api";
import { AccountingReportFilters } from "@components/accounting/types";

interface UseArAgingEntitySelectionResult {
    /** Entity ids to send to the report query \u2014 falls back to every accrual entity when the filter is empty. */
    selectedEntityIds: number[];
    /** True for entities with no accrual ledger \u2014 AR Aging requires accrual (reuses `PREMIUM_FEATURES.ACCRUAL_LEDGER`). */
    isEntityDisabled: (entity: Entity) => boolean;
    /** Non-empty accrual entity id fallback for when the user clears the entity filter (the API rejects an empty array). */
    clearedEntityIdsFallback: number[];
}

/**
 * AR Aging Summary + Detail require at least one accrual entity (same
 * `PREMIUM_FEATURES.ACCRUAL_LEDGER` capability gate as Balance Sheet / other
 * accrual reports). Mirrors General Ledger's `EntityFilterCombobox` +
 * `isEntityDisabled` pattern for disabling non-accrual entities in the
 * multi-entity picker, but simpler: AR Aging has no single `ledgerId` to
 * resolve across the selection (unlike GL), so this only needs the accrual
 * entity set, not `useGeneralLedgerLedger`'s ledger-name-matching machinery.
 */
export function useArAgingEntitySelection(
    filters: AccountingReportFilters,
): UseArAgingEntitySelectionResult {
    const allEntities = useEntities();
    const businessEntityIds = useBusinessNonMockEntityIds();
    const accrualEntityIds = useEntityIdsWithAccrualLedger(businessEntityIds);

    const clearedEntityIdsFallback = useMemo(() => {
        const validIds = businessEntityIds.filter((id) =>
            accrualEntityIds.has(id),
        );
        return validIds.length > 0 ? validIds : businessEntityIds;
    }, [businessEntityIds, accrualEntityIds]);

    const selectedEntityIds = useMemo(() => {
        if (!filters.entityIds || filters.entityIds.length === 0) {
            return clearedEntityIdsFallback;
        }
        if (accrualEntityIds.size === 0) {
            return filters.entityIds;
        }
        const accrualSelected = filters.entityIds.filter((id) =>
            accrualEntityIds.has(id),
        );
        return accrualSelected.length > 0
            ? accrualSelected
            : clearedEntityIdsFallback;
    }, [filters.entityIds, accrualEntityIds, clearedEntityIdsFallback]);

    const isEntityDisabled = useMemo(() => {
        const disabledIds = new Set(
            allEntities
                .filter((entity) => !accrualEntityIds.has(entity.id))
                .map((entity) => entity.id),
        );
        return (entity: Entity) => disabledIds.has(entity.id);
    }, [allEntities, accrualEntityIds]);

    return { selectedEntityIds, isEntityDisabled, clearedEntityIdsFallback };
}
